import {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import useDebounce from "../../../../hooks/use-debounce";
import {loadGoogleMaps} from "../../../../utils/google-maps-loader";

const StyledSearchInput = styled.div`
  position: relative;

  input {
    width: 100%;
    height: 4.4rem;
    padding: 0 4rem 0 1.2rem;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    font-size: 1.4rem;

    &:focus {
      outline: none;
      border-color: #222;
    }
  }
`;

const StyledSearchIcon = styled.span`
  position: absolute;
  right: 1.2rem;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
`;

const StyledSuggestionsPanel = styled.div`
  border: 1px solid #d9d9d9;
  border-top: none;
  border-radius: 0 0 8px 8px;
  background: #fff;
`;

const StyledSuggestionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem 1.2rem;
  font-size: 1.4rem;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

const StyledAttribution = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0.6rem 1.2rem;
  font-size: 1.1rem;
  color: #888;
`;

const StyledManualFallback = styled.div`
  padding: 1rem 1.2rem;
  font-size: 1.4rem;
`;

const StyledManualFallbackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  color: inherit;
`;

function LocationPinIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.523 11.5229L6.75 16.2959L1.977 11.5229C1.03301 10.5789 0.39014 9.3762 0.129696 8.06683C-0.130747 6.75746 0.00293013 5.40026 0.513824 4.16687C1.02472 2.93347 1.88988 1.87927 2.99991 1.13757C4.10994 0.395878 5.41498 0 6.75 0C8.08502 0 9.39006 0.395878 10.5001 1.13757C11.6101 1.87927 12.4753 2.93347 12.9862 4.16687C13.4971 5.40026 13.6308 6.75746 13.3703 8.06683C13.1099 9.3762 12.467 10.5789 11.523 11.5229V11.5229ZM6.75 9.74994C7.54565 9.74994 8.30871 9.43387 8.87132 8.87126C9.43393 8.30865 9.75 7.54559 9.75 6.74994C9.75 5.95429 9.43393 5.19123 8.87132 4.62862C8.30871 4.06601 7.54565 3.74994 6.75 3.74994C5.95435 3.74994 5.19129 4.06601 4.62868 4.62862C4.06607 5.19123 3.75 5.95429 3.75 6.74994C3.75 7.54559 4.06607 8.30865 4.62868 8.87126C5.19129 9.43387 5.95435 9.74994 6.75 9.74994V9.74994ZM6.75 8.24994C6.35218 8.24994 5.97065 8.09191 5.68934 7.8106C5.40804 7.5293 5.25 7.14777 5.25 6.74994C5.25 6.35212 5.40804 5.97059 5.68934 5.68928C5.97065 5.40798 6.35218 5.24994 6.75 5.24994C7.14783 5.24994 7.52936 5.40798 7.81066 5.68928C8.09197 5.97059 8.25 6.35212 8.25 6.74994C8.25 7.14777 8.09197 7.5293 7.81066 7.8106C7.52936 8.09191 7.14783 8.24994 6.75 8.24994Z"
        fill="#222222"
      />
    </svg>
  );
}

type TSelectedLocation = {
  address: string;
  lat: number;
  lng: number;
};

interface PlacesAutocompleteInputProps {
  value: string;
  onSelect: (location: TSelectedLocation) => void;
  /** Fired on every keystroke so the parent can invalidate a previously selected address/coordinates. */
  onQueryChange: (typedQuery: string) => void;
  onManualFallback: (typedQuery: string) => void;
}

export function PlacesAutocompleteInput({
  value,
  onSelect,
  onQueryChange,
  onManualFallback,
}: PlacesAutocompleteInputProps) {
  const [query, setQuery] = useState(value);
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [searched, setSearched] = useState(false);
  const [sdkFailed, setSdkFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(
    null
  );
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(
    null
  );
  // Skip the search effect's first run for a pre-filled value (editing a saved
  // address) so it doesn't re-search and reopen the dropdown on mount, and
  // again right after a selection, for the same reason.
  const skipNextSearchRef = useRef(Boolean(value));
  const requestIdRef = useRef(0);
  const selectionIdRef = useRef(0);
  const debouncedQuery = useDebounce(query, 350);

  useEffect(() => {
    loadGoogleMaps()
      .then((googleNamespace) => {
        autocompleteServiceRef.current =
          new googleNamespace.maps.places.AutocompleteService();
        placesServiceRef.current = new googleNamespace.maps.places.PlacesService(
          document.createElement("div")
        );
        sessionTokenRef.current =
          new googleNamespace.maps.places.AutocompleteSessionToken();
        setReady(true);
      })
      .catch((error) => {
        // Google Maps unavailable — surface it so the coach can reach manual entry.
        console.error("Failed to load Google Maps:", error);
        setSdkFailed(true);
      });
  }, []);

  useEffect(() => {
    if (!debouncedQuery.trim() || !autocompleteServiceRef.current) {
      setPredictions([]);
      setSearched(false);
      return;
    }

    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }

    const requestId = ++requestIdRef.current;

    autocompleteServiceRef.current.getPlacePredictions(
      {input: debouncedQuery, sessionToken: sessionTokenRef.current ?? undefined},
      (results, status) => {
        // A newer query has already superseded this response — ignore it.
        if (requestId !== requestIdRef.current) return;

        setSearched(true);
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
        } else {
          if (status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            console.error("Places Autocomplete request failed with status:", status);
          }
          setPredictions([]);
        }
      }
    );
    // `ready` is included so a query typed before the SDK finished loading is
    // searched once it becomes available, instead of waiting for another keystroke.
  }, [debouncedQuery, ready]);

  function handleSelectPrediction(prediction: google.maps.places.AutocompletePrediction) {
    if (!placesServiceRef.current) {
      return;
    }

    const selectionId = ++selectionIdRef.current;

    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["formatted_address", "geometry"],
        sessionToken: sessionTokenRef.current ?? undefined,
      },
      (place, status) => {
        // The coach has since edited the query or picked something else.
        if (selectionId !== selectionIdRef.current) return;

        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          place?.geometry?.location
        ) {
          const address = place.formatted_address ?? prediction.description;
          skipNextSearchRef.current = true;
          setQuery(address);
          setPredictions([]);
          // A session ends at selection — start a new one for the next search.
          sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
          onSelect({
            address,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }
      }
    );
  }

  return (
    <div>
      <StyledSearchInput>
        <input
          value={query}
          onChange={(event) => {
            const nextValue = event.target.value;
            // Invalidate any in-flight place-details lookup from a prior selection.
            selectionIdRef.current += 1;
            setQuery(nextValue);
            onQueryChange(nextValue);
          }}
          placeholder="Search location"
        />
        <StyledSearchIcon>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="10.5" cy="10.5" r="6.5" stroke="#888" strokeWidth="2" />
            <line
              x1="15.4142"
              y1="15"
              x2="21"
              y2="20.5858"
              stroke="#888"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </StyledSearchIcon>
      </StyledSearchInput>
      {predictions.length > 0 && (
        <StyledSuggestionsPanel>
          {predictions.map((prediction) => (
            <StyledSuggestionRow
              key={prediction.place_id}
              onClick={() => handleSelectPrediction(prediction)}
            >
              <LocationPinIcon />
              <span>{prediction.description}</span>
            </StyledSuggestionRow>
          ))}
          <StyledAttribution>Powered by Google</StyledAttribution>
        </StyledSuggestionsPanel>
      )}
      {(sdkFailed || searched) && predictions.length === 0 && (
        <StyledSuggestionsPanel>
          <StyledManualFallback>
            <LocationPinIcon /> Location not found. Please try again or{" "}
            <StyledManualFallbackButton
              type="button"
              onClick={() => onManualFallback(query)}
            >
              Add location manually
            </StyledManualFallbackButton>
          </StyledManualFallback>
          <StyledAttribution>Powered by Google</StyledAttribution>
        </StyledSuggestionsPanel>
      )}
    </div>
  );
}
